// Plaid credential + item storage, backed by the encrypted vault
// module. All long-lived secrets (client_id, secret, access_tokens)
// live under pf:vault:plaid:* keys; the vault encrypts them at rest
// with the user's passphrase.
//
// Two logical entries:
//   plaid:credentials   — single PlaidCredentials record (one per
//                         origin; user signs up at dashboard.plaid.com
//                         and pastes their own client_id + secret).
//   plaid:items         — PlaidItem[] array; one entry per linked
//                         institution. Storing all items in a single
//                         envelope keeps the API simple at typical
//                         scale (most users connect 1-5 banks).
//
// Non-sensitive metadata (display name, last_sync timestamp) lives
// inside the encrypted envelope too — there's no benefit to splitting
// it out into plaintext for this aggregator (unlike SimpleFIN's
// pre-unlock "Connected to X" hint, which we keep plaintext there
// because the user sees it before they ever click anything that
// triggers an unlock).
//
// Threat model: identical to vault.ts — see that file's header. The
// only thing here that's NOT in the vault is the membership-of-
// pf:vault:* keys, which leaks "the user has connected Plaid" but not
// "to which banks" or "with what tokens".

import * as vault from "../vault/vault.js";
import {
  DEFAULT_PLAID_COUNTRIES,
  PLAID_TRANSACTIONS_COUNTRIES,
  type PlaidCredentials,
  type PlaidEnv,
} from "./plaid_client.js";

const KEY_CREDS = "plaid:credentials";
const KEY_ITEMS = "plaid:items";

/** One linked institution. Created on Plaid Link onSuccess, updated
 *  on every /transactions/sync (last_cursor + last_sync), deleted on
 *  the "disconnect" action. */
export interface PlaidItem {
  /** Plaid's stable item_id, the identity Plaid keys its records by. */
  item_id: string;
  /** Long-lived access_token — used as the secret for every
   *  /transactions/sync, /item/remove call against this item. */
  access_token: string;
  /** Institution id (e.g. "ins_109511" for Chase Sandbox). Optional
   *  because some Plaid sandbox flows omit it. */
  institution_id?: string;
  /** Display name shown in the manage-banks list and as the prefix
   *  on each Transaction's "<X> (via Plaid)" label. */
  institution_name: string;
  /** Cursor threaded back into /transactions/sync to fetch only the
   *  delta since the previous call. Empty string on first sync per
   *  Plaid's docs. */
  last_cursor: string;
  /** ISO timestamp of the last successful sync. null until the first
   *  one completes. */
  last_sync: string | null;
  /** ISO timestamp of when this item was first connected via Plaid Link. */
  created_at: string;
  /** Plaid `account_id`s the user opted into during the connect flow.
   *  null means "include all accounts" (legacy items pre-dating the
   *  selector); empty array would mean "include nothing" (rejected by
   *  the UI, but allowed to round-trip if it ever lands). When set,
   *  pullPlaidSync drops every transaction whose account_id isn't in
   *  this list, and only writes the selected accounts into the
   *  synthetic JSON. */
  selected_account_ids?: string[] | null;
  /** Snapshot of the accounts the bank exposed when this item was
   *  first connected — kept so the Manage Banks UI can show
   *  account names + masks for the opt-in checkbox list without
   *  needing another Plaid round-trip. Updated each time we observe
   *  new accounts during a sync (we never silently start importing
   *  them; they appear in this list as candidates the user can
   *  toggle on later). */
  known_accounts?: KnownAccount[];
  /** Set when /transactions/sync returns ITEM_LOGIN_REQUIRED — the
   *  bank invalidated our session, usually because the user changed
   *  their bank password. The Manage Banks UI surfaces a "Re-link"
   *  button per-item in this state; the re-link flow runs Plaid Link
   *  in update mode (pass access_token to /link/token/create) so the
   *  user re-authenticates without creating a new Item. Cleared back
   *  to undefined after a successful re-link. */
  needs_relink?: boolean;
}

/** One row in the per-item account selector. Subset of Plaid's
 *  /accounts/get response — keeping just what the UI needs to render
 *  a useful label and what the filter needs to match by id. */
export interface KnownAccount {
  account_id: string;
  name: string;
  mask: string | null;
  subtype: string | null;
  type: string | null;
}

// ── credentials ────────────────────────────────────────────────────

export async function getCredentials(): Promise<PlaidCredentials | null> {
  return vault.getValue<PlaidCredentials>(KEY_CREDS);
}

export async function saveCredentials(creds: PlaidCredentials): Promise<void> {
  // Light validation — Plaid client_ids are ~24 hex chars, secrets are
  // ~30 base64 chars. Anything outside basic non-empty + recognisable
  // env is rejected here; Plaid will tell us the rest via INVALID_API_KEYS.
  if (!creds.client_id.trim()) throw new Error("Plaid client_id is required");
  if (!creds.secret.trim()) throw new Error("Plaid secret is required");
  const env: PlaidEnv = creds.env === "sandbox" ? "sandbox" : "production";

  // Normalise country_codes: trim + uppercase + drop unknowns + dedupe.
  // Fall back to the default if the result is empty (defensive — Plaid
  // requires at least one). Unknown codes don't error — Plaid will
  // reject them at /link/token/create time with a clear message.
  const supportedSet = new Set(PLAID_TRANSACTIONS_COUNTRIES.map((c) => c.code));
  const seen = new Set<string>();
  const country_codes: string[] = [];
  for (const raw of creds.country_codes ?? DEFAULT_PLAID_COUNTRIES) {
    const code = raw.trim().toUpperCase();
    if (!code || seen.has(code)) continue;
    if (!supportedSet.has(code)) continue;
    seen.add(code);
    country_codes.push(code);
  }
  if (country_codes.length === 0) country_codes.push(...DEFAULT_PLAID_COUNTRIES);

  await vault.setValue<PlaidCredentials>(KEY_CREDS, {
    client_id: creds.client_id.trim(),
    secret: creds.secret.trim(),
    env,
    country_codes,
  });
}

export async function clearCredentials(): Promise<void> {
  await vault.deleteValue(KEY_CREDS);
}

// ── items ─────────────────────────────────────────────────────────

export async function listItems(): Promise<PlaidItem[]> {
  return (await vault.getValue<PlaidItem[]>(KEY_ITEMS)) ?? [];
}

export async function getItem(itemId: string): Promise<PlaidItem | null> {
  const items = await listItems();
  return items.find((it) => it.item_id === itemId) ?? null;
}

/** Insert-or-update by item_id. Re-encrypts the whole items array on
 *  every call, which is fine at single-digit item counts. */
export async function upsertItem(item: PlaidItem): Promise<void> {
  if (!item.item_id) throw new Error("PlaidItem.item_id is required");
  const items = await listItems();
  const idx = items.findIndex((it) => it.item_id === item.item_id);
  if (idx >= 0) items[idx] = item;
  else items.push(item);
  await vault.setValue<PlaidItem[]>(KEY_ITEMS, items);
}

/** Update only the cursor + last_sync after a successful pull. Less
 *  noisy than re-passing the full record. */
export async function updateItemAfterSync(
  itemId: string,
  cursor: string,
  syncAt: Date,
): Promise<void> {
  const items = await listItems();
  const idx = items.findIndex((it) => it.item_id === itemId);
  if (idx < 0) {
    throw new Error(`No Plaid item with id ${itemId}`);
  }
  items[idx] = {
    ...items[idx],
    last_cursor: cursor,
    last_sync: syncAt.toISOString(),
    // A successful sync proves the access_token is still valid, so
    // clear any stale "needs re-link" flag set by a previous failed
    // sync. Idempotent on items that didn't have the flag.
    needs_relink: undefined,
  };
  await vault.setValue<PlaidItem[]>(KEY_ITEMS, items);
}

/** Flip an item's needs_relink flag. Called when /transactions/sync
 *  surfaces ITEM_LOGIN_REQUIRED (set true) and after a successful
 *  re-link (set false / undefined). Returns the updated item or null
 *  if no item matches. */
export async function setItemNeedsRelink(
  itemId: string,
  needs: boolean,
): Promise<PlaidItem | null> {
  const items = await listItems();
  const idx = items.findIndex((it) => it.item_id === itemId);
  if (idx < 0) return null;
  items[idx] = {
    ...items[idx],
    needs_relink: needs ? true : undefined,
  };
  await vault.setValue<PlaidItem[]>(KEY_ITEMS, items);
  return items[idx];
}

export async function deleteItem(itemId: string): Promise<void> {
  const items = await listItems();
  const filtered = items.filter((it) => it.item_id !== itemId);
  if (filtered.length === items.length) return; // not present, no-op
  await vault.setValue<PlaidItem[]>(KEY_ITEMS, filtered);
}

// ── lifecycle ─────────────────────────────────────────────────────

/** True if at least one bank is connected. Used to flip the workbench
 *  button between "Connect to Plaid" and "Pull from Plaid". */
export async function hasAnyItem(): Promise<boolean> {
  const items = await listItems();
  return items.length > 0;
}

/** Wipe all Plaid-related vault entries. Used by the "Disconnect all"
 *  action. Does NOT touch already-imported transactions in the data
 *  folder — those live with their CSV/PDF siblings. */
export async function clearAll(): Promise<void> {
  await Promise.all([vault.deleteValue(KEY_CREDS), vault.deleteValue(KEY_ITEMS)]);
}
